import React from 'react';
import { range } from 'lodash';
import { FormSelect } from 'react-bootstrap';
import { useAppSelector, useAppDispatch } from '../../app/hooks';
import {
  setStrn,
  setIntl,
  setWilp,
  setDext,
  setCont,
  setRaceId,
  setProfessionId,
  setLevel,
  setWeapon,
  setDodge,
  setShield,
  setRide,
  setWeight,
  getTrainerResults,
  setRank,
  setSelectedToggles
} from './equipmentTrainerSlice';
import Select from 'react-select';
import ReactSelectOption from './ReactSelectOption';
import ReactSelectValueContainer from './ReactSelectValueContainer';
import NumberInput from '../common/NumberInput';
import FractionalInput from '../common/FractionalInput';
import { EquipmentTrainerToggleOption } from '../../models/equipmentToggleOptions';
import { equipmentTrainerData } from './equipmentTrainerData';

const data = {
  toggleOptions: equipmentTrainerData.getToggleOptions()
};

const strnOptions = range(9, 21 + 1);
const intlOptions = range(3, 19 + 1);
const wilpOptions = range(3, 19 + 1);
const dextOptions = range(9, 19 + 1);
const contOptions = range(9, 19 + 1);
const levelOptions = range(1, 51 + 1);
const rankOptions = range(0, 9 + 1);
const rideOptions = range(0, 7 + 1);

function EquipmentTrainerHeader() {
  const dispatch = useAppDispatch();
  const strn = useAppSelector(state => state.equipmentTrainer.strn);
  const intl = useAppSelector(state => state.equipmentTrainer.intl);
  const wilp = useAppSelector(state => state.equipmentTrainer.wilp);
  const dext = useAppSelector(state => state.equipmentTrainer.dext);
  const cont = useAppSelector(state => state.equipmentTrainer.cont);
  const raceId = useAppSelector(state => state.equipmentTrainer.raceId);
  const raceOptions = useAppSelector(state => state.equipmentTrainer.raceOptions);
  const professionId = useAppSelector(state => state.equipmentTrainer.professionId);
  const professionOptions = useAppSelector(state => state.equipmentTrainer.professionOptions);
  const level = useAppSelector(state => state.equipmentTrainer.level);
  const rank = useAppSelector(state => state.equipmentTrainer.rank);
  const weapon = useAppSelector(state => state.equipmentTrainer.weapon);
  const dodge = useAppSelector(state => state.equipmentTrainer.dodge);
  const shield = useAppSelector(state => state.equipmentTrainer.shield);
  const ride = useAppSelector(state => state.equipmentTrainer.ride);
  const weight = useAppSelector(state => state.equipmentTrainer.weight);
  const mounted = useAppSelector(state => state.equipmentTrainer.mounted);
  const mountedDisabled = useAppSelector(state => state.equipmentTrainer.mountedDisabled);
  const position = useAppSelector(state => state.equipmentTrainer.position);
  const mood = useAppSelector(state => state.equipmentTrainer.mood);
  const posture = useAppSelector(state => state.equipmentTrainer.posture);
  const posturesDisabled = useAppSelector(state => state.equipmentTrainer.posturesDisabled);
  const weaponGrade = useAppSelector(state => state.equipmentTrainer.weaponGrade);
  const armorWeave = useAppSelector(state => state.equipmentTrainer.armorWeave);
  const chillWeave = useAppSelector(state => state.equipmentTrainer.chillWeave);
  const blindWeave = useAppSelector(state => state.equipmentTrainer.blindWeave);
  const contagionWeave = useAppSelector(state => state.equipmentTrainer.contagionWeave);
  const darknessWeave = useAppSelector(state => state.equipmentTrainer.darknessWeave);
  const weaving = useAppSelector(state => state.equipmentTrainer.weaving);
  const bonded = useAppSelector(state => state.equipmentTrainer.bonded);
  const flying = useAppSelector(state => state.equipmentTrainer.flying);
  const terrain = useAppSelector(state => state.equipmentTrainer.terrain);

  const toggleOptions = data.toggleOptions;
  const flatToggleOptions = data.toggleOptions.flatMap(x => x.options);

  const disabledToggleOptions: string[] = [];
  if (mountedDisabled) {
    disabledToggleOptions.push('riding-mounted');
  }
  if (posturesDisabled) {
    disabledToggleOptions.push('posture-defensive', 'posture-normal', 'posture-offensive');
  } else {
    disabledToggleOptions.push('posture-none');
  }

  const selectedToggles: EquipmentTrainerToggleOption[] = [];
  selectedToggles.push(...flatToggleOptions.filter(x => mounted && x.value === 'riding-mounted'));
  selectedToggles.push(...flatToggleOptions.filter(x => !mounted && x.value === 'riding-dismounted'));
  selectedToggles.push(...flatToggleOptions.filter(x => x.value === position));
  selectedToggles.push(...flatToggleOptions.filter(x => x.value === mood));
  selectedToggles.push(...flatToggleOptions.filter(x => x.value === posture));
  selectedToggles.push(...flatToggleOptions.filter(x => x.value === weaponGrade));
  selectedToggles.push(...flatToggleOptions.filter(x => x.value === terrain));
  selectedToggles.push(...flatToggleOptions.filter(x => flying && x.value === 'statuses-flying'));
  selectedToggles.push(...flatToggleOptions.filter(x => armorWeave && x.value === 'statuses-armor'));
  selectedToggles.push(...flatToggleOptions.filter(x => chillWeave && x.value === 'statuses-chill'));
  selectedToggles.push(...flatToggleOptions.filter(x => blindWeave && x.value === 'statuses-blind'));
  selectedToggles.push(...flatToggleOptions.filter(x => contagionWeave && x.value === 'statuses-contagion'));
  selectedToggles.push(...flatToggleOptions.filter(x => darknessWeave && x.value === 'statuses-darkness'));
  selectedToggles.push(...flatToggleOptions.filter(x => bonded && x.value === 'statuses-bonded'));
  selectedToggles.push(...flatToggleOptions.filter(x => weaving && x.value === 'statuses-weaving'));
  
  const handleStrnChange = (value: string) =>  {
    dispatch(setStrn(parseInt(value)));
    dispatch(getTrainerResults());
  }

  const handleIntlChange = (value: string) =>  {
    dispatch(setIntl(parseInt(value)));
    dispatch(getTrainerResults());
  }

  const handleWilpChange = (value: string) =>  {
    dispatch(setWilp(parseInt(value)));
    dispatch(getTrainerResults());
  }

  const handleDextChange = (value: string) =>  {
    dispatch(setDext(parseInt(value)));
    dispatch(getTrainerResults());
  }
  
  const handleContChange = (value: string) =>  {
    dispatch(setCont(parseInt(value)));
    dispatch(getTrainerResults());
  }

  const handleRaceChange = (race: string) => {
    dispatch(setRaceId(race));
    dispatch(getTrainerResults());
  }

  const handleProfessionChange = (profession: string) => {
    dispatch(setProfessionId(profession));
    dispatch(getTrainerResults());
  }

  const handleLevelChange = (value: string) =>  {
    dispatch(setLevel(parseInt(value)));
    dispatch(getTrainerResults());
  }

  const handleRankChange = (value: string) =>  {
    dispatch(setRank(parseInt(value)));
    dispatch(getTrainerResults());
  }

  const handleWeaponChange = (value: number) => {
    dispatch(setWeapon(value));
    dispatch(getTrainerResults());
  }

  const handleDodgeChange = (value: number) => {
    dispatch(setDodge(value));
    dispatch(getTrainerResults());
  }

  const handleShieldChange = (value: number) => {
    dispatch(setShield(value));
    dispatch(getTrainerResults());
  }

  const handleRideChange = (value: string) => {
    dispatch(setRide(parseInt(value)));
    dispatch(getTrainerResults());
  }

  const handleWeightChange = (value: number) => {
    dispatch(setWeight(value));
    dispatch(getTrainerResults());
  }

  const handleStatusChange = (options: EquipmentTrainerToggleOption[], data: any) => {
    options = [...options];
    
    if (data.action === 'deselect-option' && data.option.name) {      
      return;
    }

    if (data.action === 'remove-value' && data.removedValue.name) {
      const removed = data.removedValue;
      options.push(toggleOptions.flatMap(x => x.options).find(x => x.name === removed.name && !disabledToggleOptions.includes(x.value))!);
    }

    if (data.action === 'select-option' && data.option.name) {      
      const selected = data.option;
      options = options.filter((x:any) => x.name !== data.option.name || x.value === selected.value);
    }

    const result = {
      mounted: !!options.find(x => x.value === 'riding-mounted'),
      position: options.find(x => x.name === 'position')?.value,
      mood: options.find(x => x.name === 'mood')?.value,
      posture: options.find(x => x.name === 'posture')?.value,
      weaponGrade: options.find(x => x.name === 'weapongrade')?.value,
      armorWeave: !!options.find(x => x.value === 'statuses-armor'),
      chillWeave: !!options.find(x => x.value === 'statuses-chill'),
      blindWeave: !!options.find(x => x.value === 'statuses-blind'),
      contagionWeave: !!options.find(x => x.value === 'statuses-contagion'),
      darknessWeave: !!options.find(x => x.value === 'statuses-darkness'),
      weaving: !!options.find(x => x.value === 'statuses-weaving'),
      bonded: !!options.find(x => x.value === 'statuses-bonded'),
      flying: !!options.find(x => x.value === 'statuses-flying'),
      terrain: options.find(x => x.name === 'terrain')?.value,
    };

    dispatch(setSelectedToggles(result));
    dispatch(getTrainerResults());
  }

  return (
    <div>
      <div className="weave-trainer-header">
        <div className="me-2">
          <label className="small">Race</label>
          <FormSelect className="race" size="sm" aria-label="Race" value={raceId} onChange={e => handleRaceChange(e.target.value)}>
            {raceOptions.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
          </FormSelect>
        </div>

        <div className="me-2">
          <label className="small">Class</label>
          <FormSelect className="profession" size="sm" aria-label="Class" value={professionId} onChange={e => handleProfessionChange(e.target.value)}>
            {professionOptions.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
          </FormSelect>
        </div>
        <div className="me-2">
          <label className="small">Level</label>
          <FormSelect className="level" size="sm" aria-label="Level" value={level} onChange={e => handleLevelChange(e.target.value)}>
            {levelOptions.map(x => <option key={x} value={x}>{x}</option>)}
          </FormSelect>
        </div>

        <div className="me-4">
          <label className="small">Rank</label>
          <FormSelect className="w-auto" size="sm" aria-label="Class" value={rank} onChange={e => handleRankChange(e.target.value)}>
            {rankOptions.map(x => <option key={x} value={x}>{x}</option>)}
          </FormSelect>
        </div>

        <div className="me-4">
          <div className="d-flex align-items-center flex-wrap">
            <div className="me-2">
              <label className="small">Str</label>
              <FormSelect className="w-auto" size="sm" aria-label="Strength" value={strn} onChange={e => handleStrnChange(e.target.value)}>
                {strnOptions.map(x => <option key={x} value={x}>{x}</option>)}
              </FormSelect>
            </div>
            <div className="me-2">
              <label className="small">Int</label>
              <FormSelect className="w-auto" size="sm" aria-label="Intelligence" value={intl} onChange={e => handleIntlChange(e.target.value)}>
                {intlOptions.map(x => <option key={x} value={x}>{x}</option>)}
              </FormSelect>
            </div>
            <div className="me-2">
              <label className="small">Wil</label>
              <FormSelect className="w-auto" size="sm" aria-label="Willpower" value={wilp} onChange={e => handleWilpChange(e.target.value)}>
                {wilpOptions.map(x => <option key={x} value={x}>{x}</option>)}
              </FormSelect>
            </div>
            <div className="me-2">
              <label className="small">Dex</label>
              <FormSelect className="w-auto" size="sm" aria-label="Dexterity" value={dext} onChange={e => handleDextChange(e.target.value)}>
                {dextOptions.map(x => <option key={x} value={x}>{x}</option>)}
              </FormSelect>
            </div>
            <div>
              <label className="small">Con</label>
              <FormSelect className="w-auto" size="sm" aria-label="Constitution" value={cont} onChange={e => handleContChange(e.target.value)}>
                {contOptions.map(x => <option key={x} value={x}>{x}</option>)}
              </FormSelect>
            </div>
          </div>
        </div>

        <div className="me-2">
          <label className="small">Weapon</label>
          <NumberInput
            style={{width: '58px', fontSize: '.75rem'}}
            size="sm" min={0} max={99} numberValue={weapon || null}
            placeholder="%"
            onNumberChange={e => handleWeaponChange(e ?? 0)} />
        </div>

        <div className="me-2">
          <label className="small">Dodge</label>
          <NumberInput
            style={{width: '58px', fontSize: '.75rem'}}
            size="sm" min={0} max={99} numberValue={dodge || null}
            placeholder="%"
            onNumberChange={e => handleDodgeChange(e ?? 0)} />
        </div>

        <div className="me-2">
          <label className="small">Shield</label>
          <NumberInput
            style={{width: '58px', fontSize: '.75rem'}}
            size="sm" min={0} max={99} numberValue={shield || null}
            placeholder="%"
            onNumberChange={e => handleShieldChange(e ?? 0)} />
        </div>
        <div className="me-4">
          <label className="small">Ride</label>
          <FormSelect className="w-auto" size="sm" aria-label="Ride" value={ride} onChange={e => handleRideChange(e.target.value)} disabled={mountedDisabled}>
            {rideOptions.map(x => <option key={x} value={x}>{x}</option>)}
          </FormSelect>
        </div>

        <div className="me-0">
          <label className="small">Extra LB</label>
          <FractionalInput
            style={{width: '70px', fontSize: '.75rem'}}
            size="sm" min={0} max={999} numberValue={weight || null}
            placeholder="LBS"
            onNumberChange={e => handleWeightChange(e ?? 0)} />
        </div>
      </div>

      <div className="">
        <Select
          classNamePrefix="react-select"            
          value={selectedToggles}
          options={toggleOptions}
          isMulti
          unstyled
          isSearchable={false}
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          isOptionDisabled={(o:any) => disabledToggleOptions.includes(o.value)}
          components={{Option: ReactSelectOption, ValueContainer: ReactSelectValueContainer}}
          onChange={(e, options)=> handleStatusChange(e as EquipmentTrainerToggleOption[], options)}
          tabSelectsValue={false}
          />
      </div><br/>
    </div>
  );
}

export default EquipmentTrainerHeader;